/* Parfium PDP tabs — sections/pdp-tabs.liquid.

   <pdp-tabs> owns three behaviours that all live inside the same panel set:
   the tab strip, the "read more" clamp on a description, and the FAQ accordion.
   They share one element because the last two only make sense once their panel
   is visible — a hidden panel measures 0px high.

   Guarded because the file is included once per section instance, so it can be
   evaluated more than once on a page. */
if (!customElements.get('pdp-tabs')) {
  /* `<details>` gives no transition of its own, so the answer height is animated
     with Theme.motion (assets/animations.js) — the same helper the drawers and
     the FAQ section use. */
  const TOGGLE_DURATION = 0.3;
  const TOGGLE_EASING = [0.39, 0.575, 0.565, 1.0];

  /* Matches the `max-width: 989px` clamp in assets/section-pdp-tabs.css. */
  const MOBILE_QUERY = '(max-width: 989px)';

  class PdpTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.collapsibles = Array.from(this.querySelectorAll('[data-pdp-collapse]'));
      this.faqItems = Array.from(this.querySelectorAll('.pdp-faq__item'));
      /* One token per question, bumped on every toggle: an animation that
         finishes after a newer click started must not have the last word. */
      this.tokens = new WeakMap();

      this.bindTabs();
      this.bindCollapsibles();
      this.bindFaq();

      this.onResize = () => this.syncCollapsibles();
      window.addEventListener('resize', this.onResize);
      this.syncCollapsibles();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('click', this.onAnchorClick);
      window.removeEventListener('hashchange', this.onHashChange);
    }

    get motionReduced() {
      return window.Theme?.config?.motionReduced === true;
    }

    /* ------------------------------------------------------------- tabs -- */

    bindTabs() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
        tab.addEventListener('keydown', (event) => this.onKeyDown(event, tab));
      });

      /* Block anchors ([data-pdp-tab-anchor], the tab's "Anchor id") sit inside
         a hidden panel, so a plain #hash jump has nowhere to land. Any link to
         one opens its tab and scrolls to the strip instead, and so does a page
         that loads (or changes) to that hash. */
      this.onAnchorClick = (event) => {
        const link = event.target.closest('a[href*="#"]');
        if (!link || link.origin !== location.origin || link.pathname !== location.pathname) return;
        if (this.openAnchor(link.hash)) {
          event.preventDefault();
          history.pushState(null, '', link.hash);
        }
      };
      this.onHashChange = () => this.openAnchor(location.hash);
      document.addEventListener('click', this.onAnchorClick);
      window.addEventListener('hashchange', this.onHashChange);
      if (location.hash) requestAnimationFrame(() => this.openAnchor(location.hash));

      if (window.Shopify?.designMode) {
        // Selecting a block in the theme editor should reveal its tab.
        this.addEventListener('shopify:block:select', (event) => {
          const panel = event.target.closest('[role="tabpanel"]');
          if (!panel) return;
          const tab = this.tabs.find((current) => current.getAttribute('aria-controls') === panel.id);
          if (tab) this.select(tab);
        });
      }
    }

    select(tab) {
      this.tabs.forEach((current) => {
        const selected = current === tab;
        current.setAttribute('aria-selected', selected ? 'true' : 'false');
        current.setAttribute('tabindex', selected ? '0' : '-1');
        current.classList.toggle('pdp-tabs__tab--active', selected);
      });

      const controls = tab.getAttribute('aria-controls');
      this.panels.forEach((panel) => {
        panel.hidden = panel.id !== controls;
      });

      // The panel that just appeared can finally be measured.
      this.syncCollapsibles();
    }

    /* Opens the tab holding the block anchor `hash` and scrolls to the tabs.
       Returns false when the anchor is not one of this element's. */
    openAnchor(hash) {
      if (!hash || hash.length < 2) return false;

      let id = hash.slice(1);
      try {
        id = decodeURIComponent(id);
      } catch {
        // Malformed escape — match the raw text.
      }

      const anchor = document.getElementById(id);
      if (!anchor?.hasAttribute('data-pdp-tab-anchor') || !this.contains(anchor)) return false;

      const panel = anchor.closest('[role="tabpanel"]');
      const tab = this.tabs.find((current) => current.getAttribute('aria-controls') === panel?.id);
      if (!tab) return false;

      this.select(tab);

      // A panel can name a spot further down to land on when the columns stack
      // on mobile (the scent tab's notes card); otherwise the tab strip.
      const mobileTarget = window.matchMedia(MOBILE_QUERY).matches
        ? panel.querySelector('[data-pdp-anchor-mobile]')
        : null;
      const target = mobileTarget || this;
      target.scrollIntoView({ behavior: this.motionReduced ? 'auto' : 'smooth', block: 'start' });
      return true;
    }

    onKeyDown(event, tab) {
      const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      const direction = keys[event.key];
      if (!direction) return;

      event.preventDefault();
      const index = this.tabs.indexOf(tab);
      const next = this.tabs[(index + direction + this.tabs.length) % this.tabs.length];
      this.select(next);
      next.focus();
    }

    /* ------------------------------------------------ description clamp -- */

    bindCollapsibles() {
      this.collapsibles.forEach((wrap) => {
        const toggle = wrap.querySelector('[data-pdp-collapse-toggle]');
        if (!toggle) return;

        toggle.addEventListener('click', () => {
          const open = wrap.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

          const more = wrap.querySelector('[data-pdp-collapse-more]');
          const less = wrap.querySelector('[data-pdp-collapse-less]');
          if (more) more.hidden = open;
          if (less) less.hidden = !open;
        });
      });
    }

    /* Hides the button when the text already fits inside the clamp — a "read
       more" that reveals nothing reads as broken. Runs whenever the panel or
       the viewport changes, since a hidden panel measures 0px. */
    syncCollapsibles() {
      const isMobile = window.matchMedia(MOBILE_QUERY).matches;

      this.collapsibles.forEach((wrap) => {
        const toggle = wrap.querySelector('[data-pdp-collapse-toggle]');
        const clip = wrap.querySelector('.pdp-tabs__desc-clip');
        if (!toggle || !clip) return;

        // `mobile` clips only below 990px; `always` clips at every width.
        const clamped = wrap.dataset.pdpCollapse === 'always' || isMobile;

        if (!clamped) {
          toggle.hidden = true;
          return;
        }

        // A hidden panel has no layout, so leave the button alone until its tab
        // is selected — `select()` calls back in here.
        if (wrap.closest('[role="tabpanel"]')?.hidden) return;

        const styles = getComputedStyle(wrap);
        const property = isMobile ? '--pdp-tabs-collapse-h-mobile' : '--pdp-tabs-collapse-h';
        const limit = parseInt(styles.getPropertyValue(property), 10);
        const overflows = clip.scrollHeight > (Number.isNaN(limit) ? 240 : limit) + 1;

        toggle.hidden = !overflows && !wrap.classList.contains('is-open');
      });
    }

    /* -------------------------------------------------------------- FAQ -- */

    bindFaq() {
      this.faqItems.forEach((item) => {
        const summary = item.querySelector('summary');
        // No answer means no panel to animate — leave the native toggle alone.
        if (!summary || !this.panelOf(item)) return;

        summary.addEventListener('click', (event) => {
          event.preventDefault();
          this.toggleFaq(item);
        });
      });
    }

    panelOf(item) {
      return item.querySelector('[data-pdp-faq-panel]');
    }

    toggleFaq(item) {
      if (item.open) {
        this.setFaqOpen(item, false);
        return;
      }

      const list = item.closest('[data-pdp-faq]');
      if (list?.hasAttribute('data-single-open')) {
        this.faqItems.forEach((other) => {
          if (other !== item && other.open && other.closest('[data-pdp-faq]') === list) {
            this.setFaqOpen(other, false);
          }
        });
      }

      this.setFaqOpen(item, true);
    }

    /* Animates the answer between 0 and its natural height. */
    async setFaqOpen(item, open) {
      const panel = this.panelOf(item);
      const token = (this.tokens.get(item) || 0) + 1;
      this.tokens.set(item, token);

      if (!panel || this.motionReduced || !window.Theme?.motion) {
        this.resetPanel(panel);
        delete item.dataset.pdpFaqClosing;
        item.open = open;
        return;
      }

      // Measured before cancelling, so an interrupted animation hands over its
      // current height instead of jumping back to where it started.
      const from = panel.getBoundingClientRect().height;
      panel.getAnimations().forEach((animation) => animation.cancel());

      if (open) {
        delete item.dataset.pdpFaqClosing;
        item.open = true;
        panel.style.height = '';
      } else {
        // Marks the card as collapsing so its fill, stroke and caret animate out.
        item.dataset.pdpFaqClosing = '';
      }

      const to = open ? panel.getBoundingClientRect().height : 0;

      try {
        await window.Theme.motion.animate(
          panel,
          { height: [`${from}px`, `${to}px`] },
          { duration: TOGGLE_DURATION, easing: TOGGLE_EASING }
        ).finished;
      } catch {
        // Cancelling an animation rejects its finished promise. A newer toggle
        // did the cancelling, and it owns the final state from here.
        return;
      }

      if (this.tokens.get(item) !== token) return;

      this.resetPanel(panel);
      if (!open) {
        item.open = false;
        delete item.dataset.pdpFaqClosing;
      }
    }

    /* Theme.motion fills forwards, so the animation has to be dropped for the
       panel to size itself again. */
    resetPanel(panel) {
      if (!panel) return;
      panel.getAnimations().forEach((animation) => animation.cancel());
      panel.style.height = '';
    }
  }

  customElements.define('pdp-tabs', PdpTabs);
}
